/**
 * @file care-condition.schema.ts
 * @description Expressões booleanas sobre variáveis, usadas em três lugares do
 * builder de cuidado: ativação por contexto (`CareItem.activation`), regras de
 * bloco (resposta → ação) e regras de item (avaliadas ao final do fluxo).
 *
 * É um schema recursivo (AND/OR/NOT aninham condições), por isso o `z.lazy`.
 * O tipo é declarado à mão porque o Zod não infere recursão sozinho.
 *
 * Nulidade é cidadã de primeira classe: quase todo dado clínico é opcional no
 * prontuário, então `IS_KNOWN`/`IS_UNKNOWN` existem para a equipe escrever
 * regras honestas em vez de deixar o motor decidir no escuro. Ver
 * `evaluateCondition` no motor: variável ausente NUNCA satisfaz um operador de
 * comparação — só `IS_UNKNOWN`.
 */

import { z } from 'zod';

/**
 * Operadores de comparação disponíveis no editor de regras.
 * `CONTAINS`/`NOT_CONTAINS` operam sobre arrays (múltipla escolha, listas do
 * prontuário como `fatores_risco`). `COUNT_GTE` conta seleções — é o que
 * permite a regra "2+ sintomas de humor → ativa PHQ-9" do mockup.
 */
export const CareConditionOperatorEnum = z.enum([
  'EQ',
  'NEQ',
  'GT',
  'GTE',
  'LT',
  'LTE',
  'IN',
  'NOT_IN',
  'CONTAINS',
  'NOT_CONTAINS',
  'COUNT_GTE',
  'BETWEEN',
  'IS_KNOWN',
  'IS_UNKNOWN',
]);

export type CareConditionOperator = z.infer<typeof CareConditionOperatorEnum>;

/** Folha da árvore: compara uma variável (nativa ou de bloco) com um valor. */
const CareVariableConditionSchema = z.object({
  type: z.literal('VARIABLE'),
  /** Nome da variável no catálogo. Ex.: `idade_gestacional_semanas`, `tem_dmg`. */
  variable: z.string().min(1),
  operator: CareConditionOperatorEnum,
  /** Ausente para `IS_KNOWN`/`IS_UNKNOWN`; array de 2 posições para `BETWEEN`. */
  value: z.any().optional(),
});

/** Folha da árvore — a comparação em si. Exportada para os editores. */
export type CareVariableCondition = z.infer<typeof CareVariableConditionSchema>;

export type CareCondition =
  | CareVariableCondition
  | { type: 'AND'; conditions: CareCondition[] }
  | { type: 'OR'; conditions: CareCondition[] }
  | { type: 'NOT'; condition: CareCondition };

export const CareConditionSchema: z.ZodType<CareCondition> = z.lazy(() =>
  z.union([
    CareVariableConditionSchema,
    z.object({ type: z.literal('AND'), conditions: z.array(CareConditionSchema).min(1) }),
    z.object({ type: z.literal('OR'), conditions: z.array(CareConditionSchema).min(1) }),
    z.object({ type: z.literal('NOT'), condition: CareConditionSchema }),
  ])
);

export { CareVariableConditionSchema };
